/**
 * The room console on the table (redesigned 2026-10-05, the user's request): basic filters on
 * the left (a labelled 3D token per site, paged for studies with many sites, then arm, sex,
 * subject status and date chips) and live thumbnails of the study summary and all eleven
 * dashboards on the right. Selecting a thumbnail shows that view on the centre screen, so the
 * whole review can run from the table. Plus a visit rail under Visit Compliance and a date
 * strip under Query Management. Filters apply to every screen of the current user (still per
 * user; "Sync filters with room" shares them).
 */
import * as THREE from 'three';
import type { DashboardFilters, Site } from '@clinspace/shared';
import { CanvasPanel, type Region } from './CanvasPanel.ts';
import type { CentreView } from './CentreScreen.ts';
import {
  PALETTE,
  SERIES,
  chip,
  fillRoundRect,
  font,
  glassPanel,
  roundRect,
  text,
  truncate,
} from './charts.ts';
import { StudyView } from './StudyView.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { ROOM } from '../world/StudyRoom.ts';

/** A view the console can put on the centre screen, with its live canvas. */
export interface ConsoleScreen {
  id: CentreView;
  title: string;
  canvas: HTMLCanvasElement;
  /** Changes whenever the view's content changes (the thumbnail is redrawn then). */
  version: number;
}

export interface RoomFiltersHandlers {
  apply(patch: Partial<DashboardFilters>): void;
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  /** Every dashboard back to its overview and its frame, the centre screen to the summary. */
  resetScreens(): void;
  /** The summary and the eleven dashboards, in console order. */
  screens(): ConsoleScreen[];
  /** What the centre screen shows. */
  onScreen(): CentreView;
  /** Shows a view on the centre screen. */
  project(view: CentreView): void;
}

/** Console size on the table, in metres and logical canvas pixels; tilt in radians. */
const CONSOLE = { w: 1.8, h: 0.72, pxW: 1440, pxH: 576, tilt: 0.38 };
const COLS = 4;
const ROWS = 2;
/** Token slots per page: the first is always "All sites". */
export const SLOTS_PER_PAGE = COLS * ROWS;
/** The site tokens' block in the filters column. */
const GRID = { x: 24, y: 144, w: 616, h: 164 };
const TOKEN_R = 0.03;
/** The filters column's chip rows and the thumbnails' block. */
const ROW0 = 322;
const ROW_H = 54;
const CHIP_X = 100;
const THUMBS = { x: 680, y: 108, w: 736, cols: 4, gap: 12, label: 26 };
/** Statuses offered as chips (the ward console has every status). */
const STATUS_CHIPS = ['On Treatment', 'Completed', 'Discontinued', 'Screen Failed'] as const;
/** Thumbnails refresh this long after a view changed, so its canvas has been redrawn. */
const THUMB_DELAY_MS = 250;

/** Which sites a page shows: "All sites" first, then up to SLOTS_PER_PAGE - 1 sites. */
export function sitePage(
  count: number,
  page: number,
): { start: number; end: number; pages: number; page: number } {
  const per = SLOTS_PER_PAGE - 1;
  const pages = Math.max(1, Math.ceil(count / per));
  const p = Math.min(Math.max(0, page), pages - 1);
  return { start: p * per, end: Math.min(count, (p + 1) * per), pages, page: p };
}

/** Canvas centre of the token in slot i (0 is "All sites") and the slot's cell size. */
export function slotCentre(i: number): { x: number; y: number; w: number; h: number } {
  const w = GRID.w / COLS;
  const h = GRID.h / ROWS;
  return { x: GRID.x + w * ((i % COLS) + 0.5), y: GRID.y + h * Math.floor(i / COLS) + 28, w, h };
}

/** The thumbnail cell for console screen i (image rectangle plus its label row). */
export function thumbRect(i: number): { x: number; y: number; w: number; h: number } {
  const w = (THUMBS.w - THUMBS.gap * (THUMBS.cols - 1)) / THUMBS.cols;
  const h = w / (ROOM.panelWidth / ROOM.panelHeight);
  const col = i % THUMBS.cols;
  const row = Math.floor(i / THUMBS.cols);
  return {
    x: THUMBS.x + col * (w + THUMBS.gap),
    y: THUMBS.y + row * (h + THUMBS.label + THUMBS.gap),
    w,
    h,
  };
}

interface ConsoleRegion {
  action?: 'reset' | 'clear' | 'prev' | 'next';
  slot?: number;
  patch?: Partial<DashboardFilters>;
  screen?: CentreView;
}

export class RoomFilters {
  readonly group = new THREE.Group();
  readonly tokens: THREE.InstancedMesh;
  readonly plate: CanvasPanel;
  readonly visitRail: CanvasPanel;
  readonly dateStrip: CanvasPanel;
  filters: DashboardFilters = {};
  /** Current page of site tokens (for studies with many sites). */
  page = 0;
  private readonly sites: Site[];
  /** Site shown in each visible slot; undefined for "All sites". */
  private slotSites: (Site | undefined)[] = [];
  private hoveredToken = -1;
  private pendingFrom: string | undefined;
  private screensKey = '';
  private thumbsDue: number | undefined;

  constructor(
    private readonly view: StudyView,
    private readonly interactive: InteractiveSystem,
    private readonly handlers: RoomFiltersHandlers,
  ) {
    this.sites = view.file.sites;
    this.tokens = new THREE.InstancedMesh(
      new THREE.CylinderGeometry(TOKEN_R, TOKEN_R * 1.08, 0.022, 24),
      new THREE.MeshStandardMaterial({
        roughness: 0.35,
        metalness: 0.15,
        emissive: 0xffffff,
        emissiveIntensity: 0.08,
        toneMapped: false,
      }),
      SLOTS_PER_PAGE,
    );
    this.tokens.name = 'site-tokens';
    this.tokens.frustumCulled = false;
    this.group.add(this.tokens);
    interactive.add({
      object: this.tokens,
      touchable: true,
      onHover: (_p, point, hit) => {
        const i = hit?.instanceId ?? -1;
        if (i === this.hoveredToken) return;
        this.hoveredToken = i;
        handlers.tooltip(i < 0 ? undefined : this.slotTooltip(i), point);
      },
      onLeave: () => {
        this.hoveredToken = -1;
        handlers.tooltip(undefined, undefined);
      },
      onSelect: (_p, _point, hit) => {
        const i = hit?.instanceId ?? -1;
        if (i >= 0) this.selectSlot(i);
      },
    });

    // The console lies on the table's entrance side, tilted up like a lectern, so whoever
    // stands there looks over it at the centre screen.
    this.plate = new CanvasPanel({
      width: CONSOLE.w,
      height: CONSOLE.h,
      pixelWidth: CONSOLE.pxW,
      pixelHeight: CONSOLE.pxH,
      name: 'room-console',
      resolution: 1.25,
    });
    this.plate.mesh.rotation.x = -Math.PI / 2 + CONSOLE.tilt;
    this.plate.mesh.position.set(
      ROOM.tableCentre.x,
      0.965 + (CONSOLE.h / 2) * Math.sin(CONSOLE.tilt),
      ROOM.tableCentre.z + 0.66,
    );
    this.plate.mesh.updateMatrix();
    this.plate.setDraw((ctx, panel) => this.drawPlate(ctx, panel));
    this.plate.onSelect = (region) => this.selectConsole(region);
    this.plate.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.group.add(this.plate.mesh);

    this.visitRail = new CanvasPanel({
      width: 2.2,
      height: 0.14,
      pixelWidth: 1100,
      pixelHeight: 70,
      name: 'visit-rail',
      resolution: 1.5,
    });
    this.visitRail.setDraw((ctx, panel) => this.drawVisitRail(ctx, panel));
    this.visitRail.onSelect = (region) => this.selectVisit(region);
    this.visitRail.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.dateStrip = new CanvasPanel({
      width: 2.2,
      height: 0.14,
      pixelWidth: 1100,
      pixelHeight: 70,
      name: 'date-strip',
      resolution: 1.5,
    });
    this.dateStrip.setDraw((ctx, panel) => this.drawDateStrip(ctx, panel));
    this.dateStrip.onSelect = (region) => this.selectDate(region);
    this.dateStrip.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.plate.attach(interactive);
    this.visitRail.attach(interactive);
    this.dateStrip.attach(interactive);
    this.layoutTokens();
  }

  /** Hangs the rail and the strip under their dashboards (panel-local placement). */
  attachRails(visitGroup: THREE.Object3D, queryGroup: THREE.Object3D): void {
    this.visitRail.mesh.position.set(0, -0.9, 0.04);
    visitGroup.add(this.visitRail.mesh);
    this.dateStrip.mesh.position.set(0, -0.9, 0.04);
    queryGroup.add(this.dateStrip.mesh);
  }

  setFilters(f: DashboardFilters): void {
    this.filters = { ...f };
    this.pendingFrom = undefined;
    // Keep the active site's token on screen.
    if (f.siteId) {
      const index = this.sites.findIndex((s) => s.id === f.siteId);
      if (index >= 0) this.page = Math.floor(index / (SLOTS_PER_PAGE - 1));
    }
    this.layoutTokens();
    this.plate.invalidate();
    this.visitRail.invalidate();
    this.dateStrip.invalidate();
  }

  update(now = performance.now()): void {
    // Thumbnails follow the views they show: when one changes, redraw the console shortly
    // after (its canvas is redrawn first). Never per frame.
    const key = `${this.handlers.onScreen()}|${this.handlers
      .screens()
      .map((s) => s.version)
      .join(',')}`;
    if (key !== this.screensKey) {
      this.screensKey = key;
      this.thumbsDue ??= now + THUMB_DELAY_MS;
    }
    if (this.thumbsDue !== undefined && now >= this.thumbsDue) {
      this.thumbsDue = undefined;
      this.plate.invalidate();
    }
    this.plate.update();
    this.visitRail.update();
    this.dateStrip.update();
  }

  /** Labels of the tokens on the current page (dev hooks). */
  tokenLabels(): string[] {
    return this.slotSites.map((s) => (s ? `${s.id} ${s.city}` : 'All sites'));
  }

  setPage(page: number): void {
    this.page = sitePage(this.sites.length, page).page;
    this.layoutTokens();
    this.plate.invalidate();
  }

  selectSlot(i: number): void {
    if (i < 0 || i >= this.slotSites.length) return;
    const site = this.slotSites[i];
    const next = site && this.filters.siteId !== site.id ? site.id : undefined;
    this.handlers.apply({ siteId: next });
  }

  private slotTooltip(i: number): string[] | undefined {
    if (i >= this.slotSites.length) return undefined;
    const site = this.slotSites[i];
    if (!site) return ['All sites', 'Select to clear the site filter'];
    const n = this.view.file.subjects.filter((s) => s.siteId === site.id).length;
    return [
      `${site.id} ${site.city}, ${site.country}`,
      `${n} subjects`,
      this.filters.siteId === site.id
        ? 'Filtering the room to this site; select to clear'
        : 'Select to filter every dashboard to this site',
    ];
  }

  private layoutTokens(): void {
    const { start, end } = sitePage(this.sites.length, this.page);
    this.slotSites = [undefined, ...this.sites.slice(start, end)];
    const local = new THREE.Matrix4();
    const upright = new THREE.Matrix4().makeRotationX(Math.PI / 2);
    const scale = new THREE.Matrix4();
    const colour = new THREE.Color();
    this.plate.mesh.updateMatrix();
    this.slotSites.forEach((site, i) => {
      const c = slotCentre(i);
      const active = site ? this.filters.siteId === site.id : !this.filters.siteId;
      // Canvas pixels to console-local metres; the token stands on the console face.
      const lx = (c.x / CONSOLE.pxW - 0.5) * CONSOLE.w;
      const ly = (0.5 - c.y / CONSOLE.pxH) * CONSOLE.h;
      local.makeTranslation(lx, ly, 0.013 + (active ? 0.012 : 0));
      const s = active ? 1.15 : 1;
      scale.makeScale(s, s, s);
      const m = this.plate.mesh.matrix.clone().multiply(local).multiply(upright).multiply(scale);
      this.tokens.setMatrixAt(i, m);
      const siteIndex = site ? this.sites.indexOf(site) : -1;
      this.tokens.setColorAt(
        i,
        colour
          .set(site ? SERIES[siteIndex % SERIES.length]! : PALETTE.muted)
          .multiplyScalar(active ? 1.15 : 0.8),
      );
    });
    this.tokens.count = this.slotSites.length;
    this.tokens.instanceMatrix.needsUpdate = true;
    if (this.tokens.instanceColor) this.tokens.instanceColor.needsUpdate = true;
    this.tokens.computeBoundingSphere();
  }

  private drawPlate(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    ctx.clearRect(0, 0, W, H);
    glassPanel(ctx, { x: 2, y: 2, w: W - 4, h: H - 4 }, 22);
    text(ctx, 'ROOM CONSOLE', 24, 40, { size: 15, colour: PALETTE.accentText, weight: 700 });
    ctx.font = font(15, 700);
    text(ctx, this.view.code, 24 + ctx.measureText('ROOM CONSOLE').width + 16, 40, {
      size: 15,
      colour: PALETTE.muted,
      weight: 600,
    });
    // Actions on the header row, right-aligned.
    ctx.font = font(15, 600);
    const resetW = ctx.measureText('Reset all screens').width + 40;
    const reset = chip(ctx, 'Reset all screens', W - 24 - resetW, 16, {
      active: true,
      hovered: panel.isHovered('reset'),
      h: 40,
      size: 15,
      colour: PALETTE.teal,
    });
    panel.region<ConsoleRegion>({
      id: 'reset',
      ...reset,
      data: { action: 'reset' },
      tooltip: [
        'Reset all screens',
        'Every dashboard back to its overview and its frame; the centre screen to the summary',
      ],
    });
    const clearW = ctx.measureText('Clear filters').width + 40;
    const clear = chip(ctx, 'Clear filters', reset.x - 14 - clearW, 16, {
      hovered: panel.isHovered('clear'),
      h: 40,
      size: 15,
    });
    panel.region<ConsoleRegion>({
      id: 'clear',
      ...clear,
      data: { action: 'clear' },
      tooltip: ['Clear filters', 'Every filter back to all, on every screen'],
    });
    ctx.fillStyle = PALETTE.track;
    ctx.fillRect(24, 70, W - 48, 1.5);
    ctx.fillRect(660, 84, 1.5, H - 108);

    this.drawFilters(ctx, panel);
    this.drawThumbnails(ctx, panel);
  }

  private drawFilters(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const f = this.filters;
    text(ctx, 'FILTERS', 24, 98, { size: 12, colour: PALETTE.muted, weight: 700 });
    text(ctx, `Showing ${describeShort(f, this.view)}`, CHIP_X, 98, {
      size: 12,
      weight: 600,
      colour: StudyView.isFiltered(f) ? PALETTE.teal : PALETTE.textSoft,
      maxWidth: 300,
    });
    // Site tokens: a label cell under each 3D token, paging for large studies.
    this.slotSites.forEach((site, i) => {
      const c = slotCentre(i);
      const id = `slot:${i}`;
      const active = site ? f.siteId === site.id : !f.siteId;
      const cell = { x: c.x - c.w / 2 + 4, y: c.y - 26, w: c.w - 8, h: c.h - 4 };
      if (active) fillRoundRect(ctx, cell, 12, PALETTE.selected);
      else if (panel.isHovered(id)) fillRoundRect(ctx, cell, 12, PALETTE.hover);
      ctx.font = font(13, 700);
      text(ctx, truncate(ctx, site ? site.id : 'All', c.w - 16), c.x, c.y + 34, {
        size: 13,
        weight: 700,
        align: 'center',
      });
      ctx.font = font(10, 500);
      text(ctx, truncate(ctx, site ? site.city : 'sites', c.w - 16), c.x, c.y + 50, {
        size: 10,
        colour: PALETTE.muted,
        align: 'center',
      });
      panel.region<ConsoleRegion>({ id, ...cell, data: { slot: i }, tooltip: this.slotTooltip(i) });
    });
    const { start, end, pages } = sitePage(this.sites.length, this.page);
    if (pages > 1) {
      ctx.font = font(12, 600);
      const nextW = ctx.measureText('Next ›').width + 40;
      const next = chip(ctx, 'Next ›', 640 - nextW, 80, {
        hovered: panel.isHovered('next'),
        h: 30,
        size: 12,
      });
      const prevW = ctx.measureText('‹ Prev').width + 40;
      const prev = chip(ctx, '‹ Prev', next.x - 8 - prevW, 80, {
        hovered: panel.isHovered('prev'),
        h: 30,
        size: 12,
      });
      text(ctx, `Sites ${start + 1}–${end} of ${this.sites.length}`, prev.x - 10, 100, {
        size: 10,
        colour: PALETTE.muted,
        align: 'right',
      });
      panel.region<ConsoleRegion>({ id: 'prev', ...prev, data: { action: 'prev' } });
      panel.region<ConsoleRegion>({ id: 'next', ...next, data: { action: 'next' } });
    }
    // Chip rows: arm, sex, status, dates. The first chip of a row clears that filter.
    rowsFor(f, this.view.arms(), this.datePresets()).forEach((row, r) => {
      const y = ROW0 + r * ROW_H;
      text(ctx, row.label.toUpperCase(), 24, y + 24, {
        size: 11,
        weight: 700,
        colour: PALETTE.muted,
      });
      // Chips share the row; long labels (study arms) shorten to fit.
      const room = 640 - CHIP_X - 8 * (row.options.length - 1);
      ctx.font = font(11, 600);
      const natural = row.options.map((o) => ctx.measureText(o.label).width + 20);
      const total = natural.reduce((a, b) => a + b, 0);
      const k = total > room ? room / total : 1;
      let x = CHIP_X;
      row.options.forEach((o, i) => {
        const id = `chip:${r}:${i}`;
        const w = natural[i]! * k;
        ctx.font = font(11, 600);
        const c = chip(ctx, truncate(ctx, o.label, w - 20), x, y, {
          active: o.active,
          hovered: panel.isHovered(id),
          h: 36,
          size: 11,
          minW: w,
        });
        panel.region<ConsoleRegion>({
          id,
          ...c,
          data: { patch: o.patch },
          tooltip: [
            `${row.label}: ${o.label}`,
            o.active ? 'Applied to every screen' : 'Select to filter every screen',
          ],
        });
        x += c.w + 8;
      });
    });
    text(
      ctx,
      'Filters apply to every screen you see; "Sync filters" on a dashboard shares them.',
      24,
      552,
      { size: 10, colour: PALETTE.muted, maxWidth: 620 },
    );
  }

  private drawThumbnails(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const on = this.handlers.onScreen();
    text(ctx, 'SHOW ON THE CENTRE SCREEN', THUMBS.x, 98, {
      size: 12,
      weight: 700,
      colour: PALETTE.muted,
    });
    this.handlers.screens().forEach((screen, i) => {
      const r = thumbRect(i);
      const id = `screen:${screen.id}`;
      const active = screen.id === on;
      const hovered = panel.isHovered(id);
      ctx.save();
      roundRect(ctx, r.x, r.y, r.w, r.h, 8);
      ctx.clip();
      ctx.fillStyle = PALETTE.raised;
      ctx.fillRect(r.x, r.y, r.w, r.h);
      if (screen.canvas.width > 0) {
        // Whole and unsquashed: the summary's canvas is wider than a dashboard's (2026-10-07).
        const c = screen.canvas;
        const k = Math.min(r.w / c.width, r.h / c.height);
        const w = c.width * k;
        const h = c.height * k;
        ctx.drawImage(c, r.x + (r.w - w) / 2, r.y + (r.h - h) / 2, w, h);
      }
      if (hovered && !active) {
        ctx.fillStyle = PALETTE.hover;
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
      ctx.restore();
      ctx.lineWidth = active ? 4 : hovered ? 2.5 : 1.2;
      ctx.strokeStyle = active ? PALETTE.teal : hovered ? PALETTE.accentText : PALETTE.line;
      roundRect(ctx, r.x, r.y, r.w, r.h, 8);
      ctx.stroke();
      if (active) {
        const badge = { x: r.x + 6, y: r.y + 6, w: 92, h: 22 };
        fillRoundRect(ctx, badge, 11, PALETTE.teal);
        text(ctx, 'ON SCREEN', badge.x + badge.w / 2, badge.y + 16, {
          size: 9,
          weight: 700,
          colour: PALETTE.onAccent,
          align: 'center',
        });
      }
      ctx.font = font(11, 600);
      text(ctx, truncate(ctx, screen.title, r.w - 4), r.x + r.w / 2, r.y + r.h + 19, {
        size: 11,
        weight: 600,
        align: 'center',
        colour: active ? PALETTE.teal : PALETTE.text,
      });
      panel.region<ConsoleRegion>({
        id,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h + THUMBS.label,
        data: { screen: screen.id },
        tooltip: active
          ? [screen.title, 'On the centre screen; select to return it to its overview']
          : [screen.title, 'Select to show it on the centre screen'],
      });
    });
  }

  /** Dev hook: what the console offers for the centre screen. */
  screenIds(): CentreView[] {
    return this.handlers.screens().map((s) => s.id);
  }

  /** Dev hooks: the console's regions, selecting one without a pointer, and where it is. */
  regionIds(): string[] {
    return this.plate.allRegions().map((r) => r.id);
  }

  selectRegion(id: string): boolean {
    const region = this.plate.allRegions().find((r) => r.id === id);
    if (!region) return false;
    this.selectConsole(region);
    return true;
  }

  regionWorld(id: string): THREE.Vector3 | undefined {
    const r = this.plate.allRegions().find((x) => x.id === id);
    return r ? this.plate.toWorld(r.x + r.w / 2, r.y + r.h / 2) : undefined;
  }

  private selectConsole(region: Region): void {
    const data = region.data as ConsoleRegion | undefined;
    if (!data) return;
    if (data.slot !== undefined) this.selectSlot(data.slot);
    else if (data.screen) this.handlers.project(data.screen);
    else if (data.patch) this.handlers.apply(data.patch);
    else if (data.action === 'reset') this.handlers.resetScreens();
    else if (data.action === 'clear')
      this.handlers.apply({
        siteId: undefined,
        arm: undefined,
        visitFrom: undefined,
        visitTo: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        subjectId: undefined,
        subjectIds: undefined,
        sex: undefined,
        status: undefined,
        flag: undefined,
        ageBand: undefined,
        bmiBucket: undefined,
      });
    else if (data.action === 'prev') this.setPage(this.page - 1);
    else if (data.action === 'next') this.setPage(this.page + 1);
  }

  private drawVisitRail(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 10, PALETTE.surface);
    text(ctx, 'VISIT RANGE', 14, 24, { size: 11, colour: PALETTE.muted, weight: 600 });
    const codes = this.view.file.study.visitSchedule.map((v) => v.code);
    const x0 = 120;
    const slot = (W - x0 - 20) / codes.length;
    const fromI = this.filters.visitFrom ? codes.indexOf(this.filters.visitFrom) : 0;
    const toI = this.filters.visitTo ? codes.indexOf(this.filters.visitTo) : codes.length - 1;
    const pendingI = this.pendingFrom ? codes.indexOf(this.pendingFrom) : -1;
    ctx.fillStyle = PALETTE.grid;
    ctx.fillRect(x0, 34, W - x0 - 20, 4);
    if (this.filters.visitFrom || this.filters.visitTo) {
      ctx.fillStyle = PALETTE.teal;
      ctx.fillRect(x0 + fromI * slot + slot / 2, 33, (toI - fromI) * slot, 6);
    }
    codes.forEach((c, i) => {
      const cx = x0 + slot * (i + 0.5);
      const active = i >= fromI && i <= toI && (this.filters.visitFrom || this.filters.visitTo);
      const id = `visit:${i}`;
      ctx.beginPath();
      ctx.arc(cx, 36, panel.isHovered(id) ? 9 : 7, 0, Math.PI * 2);
      ctx.fillStyle = i === pendingI ? PALETTE.amber : active ? PALETTE.teal : PALETTE.idle;
      ctx.fill();
      text(ctx, c, cx, 60, { size: 10, colour: PALETTE.muted, align: 'center' });
      panel.region({
        id,
        x: cx - slot / 2,
        y: 10,
        w: slot,
        h: H - 10,
        data: { code: c },
        tooltip: [
          c,
          this.pendingFrom
            ? `Range ${this.pendingFrom} to ${c}`
            : 'Select the first visit of a range, then the last',
        ],
      });
    });
    text(
      ctx,
      this.pendingFrom
        ? `from ${this.pendingFrom} …`
        : this.filters.visitFrom || this.filters.visitTo
          ? `${this.filters.visitFrom ?? 'start'} – ${this.filters.visitTo ?? 'end'}`
          : 'all visits',
      14,
      48,
      { size: 11, colour: this.pendingFrom ? PALETTE.amber : PALETTE.text },
    );
  }

  private selectVisit(region: Region): void {
    const code = (region.data as { code?: string } | undefined)?.code;
    if (!code) return;
    const codes = this.view.file.study.visitSchedule.map((v) => v.code);
    if (!this.pendingFrom) {
      if (this.filters.visitFrom || this.filters.visitTo) {
        // A range is set: one select clears it.
        this.handlers.apply({ visitFrom: undefined, visitTo: undefined });
        return;
      }
      this.pendingFrom = code;
      this.visitRail.invalidate();
      return;
    }
    const a = codes.indexOf(this.pendingFrom);
    const b = codes.indexOf(code);
    const [from, to] = a <= b ? [this.pendingFrom, code] : [code, this.pendingFrom];
    this.pendingFrom = undefined;
    this.handlers.apply({ visitFrom: from, visitTo: to });
  }

  private datePresets(): { label: string; from?: string }[] {
    const anchor = this.view.file.anchorDate;
    const minus = (days: number) =>
      new Date(Date.parse(anchor) - days * 86_400_000).toISOString().slice(0, 10);
    return [
      { label: 'All dates' },
      { label: 'Last 30 days', from: minus(30) },
      { label: 'Last 90 days', from: minus(90) },
      { label: 'Last 6 months', from: minus(182) },
      { label: 'Last 12 months', from: minus(365) },
    ];
  }

  private drawDateStrip(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    fillRoundRect(ctx, { x: 0, y: 0, w: W, h: H }, 10, PALETTE.surface);
    text(ctx, 'DATE RANGE', 14, 24, { size: 11, colour: PALETTE.muted, weight: 600 });
    text(ctx, 'to the data cut', 14, 48, { size: 11, colour: PALETTE.muted });
    let x = 130;
    this.datePresets().forEach((p, i) => {
      const id = `date:${i}`;
      const active = (this.filters.dateFrom ?? '') === (p.from ?? '') && !this.filters.dateTo;
      const r = chip(ctx, p.label, x, 18, {
        active,
        hovered: panel.isHovered(id),
        h: 32,
        size: 14,
        minW: 150,
      });
      panel.region({
        id,
        ...r,
        data: { from: p.from ?? null },
        tooltip: [
          p.label,
          p.from ? `Records dated ${p.from} or later, on every dashboard` : 'Clear the date range',
        ],
      });
      x += r.w + 12;
    });
    void H;
  }

  private selectDate(region: Region): void {
    const data = region.data as { from?: string | null } | undefined;
    if (!data) return;
    this.handlers.apply({ dateFrom: data.from ?? undefined, dateTo: undefined });
  }

  dispose(): void {
    this.plate.detach(this.interactive);
    this.visitRail.detach(this.interactive);
    this.dateStrip.detach(this.interactive);
    this.plate.dispose();
    this.visitRail.dispose();
    this.dateStrip.dispose();
    this.tokens.geometry.dispose();
    (this.tokens.material as THREE.Material).dispose();
  }
}

interface ChipRow {
  label: string;
  options: { label: string; patch: Partial<DashboardFilters>; active: boolean }[];
}

/** The console's chip rows under the current filter: arm, sex, subject status, dates. */
export function rowsFor(
  f: DashboardFilters,
  arms: string[],
  dates: { label: string; from?: string }[],
): ChipRow[] {
  return [
    {
      label: 'Arm',
      options: [
        { label: 'All arms', patch: { arm: undefined }, active: !f.arm },
        ...arms.map((a) => ({ label: a, patch: { arm: a }, active: f.arm === a })),
      ],
    },
    {
      label: 'Sex',
      options: [
        { label: 'All', patch: { sex: undefined }, active: !f.sex },
        { label: 'Female', patch: { sex: 'F' }, active: f.sex === 'F' },
        { label: 'Male', patch: { sex: 'M' }, active: f.sex === 'M' },
      ],
    },
    {
      label: 'Status',
      options: [
        { label: 'All', patch: { status: undefined }, active: !f.status },
        ...STATUS_CHIPS.map((st) => ({
          label: st,
          patch: { status: st },
          active: f.status === st,
        })),
      ],
    },
    {
      label: 'Dates',
      options: dates
        .filter((p) => p.label !== 'Last 6 months')
        .map((p) => ({
          label: p.label,
          patch: { dateFrom: p.from, dateTo: undefined },
          active: (f.dateFrom ?? '') === (p.from ?? '') && !f.dateTo,
        })),
    },
  ];
}

/** The room filter in a few words, for the console's header line. */
function describeShort(f: DashboardFilters, view: StudyView): string {
  const parts: string[] = [];
  if (f.siteId) parts.push(view.siteLabel(f.siteId));
  if (f.arm) parts.push(f.arm);
  if (f.sex) parts.push(f.sex === 'F' ? 'female' : 'male');
  if (f.status) parts.push(f.status.toLowerCase());
  if (f.subjectIds?.length) parts.push(`under review ${f.subjectIds.join(', ')}`);
  if (f.subjectId) parts.push(`subject ${f.subjectId}`);
  for (const k of ['flag', 'ageBand', 'bmiBucket'] as const)
    if (f[k]) parts.push(`${k === 'ageBand' ? 'age ' : k === 'bmiBucket' ? 'BMI ' : ''}${f[k]}`);
  if (f.visitFrom || f.visitTo)
    parts.push(`visits ${f.visitFrom ?? 'start'}–${f.visitTo ?? 'end'}`);
  if (f.dateFrom) parts.push(`from ${f.dateFrom}`);
  return parts.length ? parts.join(' · ') : 'the whole study';
}
